import { Component, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TagFilterDropdownComponent } from '../../atoms/tag-filter-dropdown/tag-filter-dropdown.component';
import {
  TriStateCheckboxChangeEvent,
  TriStateCheckboxModule,
} from 'primeng/tristatecheckbox';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { StateFilter } from '../../../stores/crud.state';
import { NgClass, NgIf } from '@angular/common';
import { TreeSelectModule } from 'primeng/treeselect';
import { CategoryStore } from '../../../stores/category/category.state';
import { TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-indicator-filter',
  standalone: true,
  imports: [
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ReactiveFormsModule,
    TagFilterDropdownComponent,
    TriStateCheckboxModule,
    FormsModule,
    NgIf,
    TreeSelectModule,
    NgClass,
    TooltipModule,
  ],
  templateUrl: './indicator-filter.component.html',
  styleUrl: './indicator-filter.component.scss',
})
export class IndicatorFilterComponent {
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly dataStore = inject(IndicatorDataStore);

  filterQuery: StateFilter;
  constructor() {
    this.filterQuery = {
      query: '',
      ids: undefined,
      locked: null,
      published: null,
      category: [],
      tags: [],
      products: [],
    };
  }

  /**
   * Adds or removes a category and its child categories recursively based on the provided node.
   *
   * @param {TreeNode} node - The tree node representing a category. This node and its children will be processed.
   * @param {boolean} [remove] - A flag indicating whether to remove the category and its children.
   *                              If true, the category is removed; otherwise, it is added. Default is false.
   *
   * @return {void}
   */
  private addOrRemoveCategoryAndChildren(
    node: TreeNode,
    remove?: boolean
  ): void {
    if (node.key && !remove) this.addCategoryId(node.key);
    if (node.key && remove) this.removeCategoryId(node.key);
    node.children?.forEach(childNode => {
      if (node.children) {
        this.addOrRemoveCategoryAndChildren(childNode, remove);
      }
    });
  }

  /**
   * Adds a category ID to the filter query's category list.
   *
   * @param {string} id - The category ID to be added.
   * @return {void}
   */
  addCategoryId(id: string): void {
    this.filterQuery.category.push(id);
  }

  /**
   * Removes the specified category ID from the filter query's category array.
   *
   * @param {string} id - The ID of the category to be removed.
   * @return {void} This method does not return a value.
   */
  removeCategoryId(id: string): void {
    this.filterQuery.category.splice(this.filterQuery.category.indexOf(id), 1);
  }

  /**
   * Adds a category and its child nodes to the query or removes them based on the event.
   *
   * @param {TreeNodeSelectEvent} event - The event triggered when a tree node is selected or deselected. Contains information about the selected node.
   * @return {void}
   */
  addCategoryToQuery(event: TreeNodeSelectEvent): void {
    this.addOrRemoveCategoryAndChildren(event.node);
    this.updateFilter();
  }

  /**
   * Removes a category and its children from the current query based on the tree node unselection event.
   *
   * @param {TreeNodeUnSelectEvent} event - The event object triggered when a tree node is unselected.
   * @return {void} This method does not return a value.
   */
  removeCategoryFromQuery(event: TreeNodeUnSelectEvent): void {
    this.addOrRemoveCategoryAndChildren(event.node, true);
    this.updateFilter();
  }

  /**
   * Updates the filter query applied in the indicator store asynchronously.
   * This method sets a small delay before executing the filter update.
   *
   * @return {void} Does not return a value.
   */
  updateFilter(): void {
    setTimeout(() => {
      this.indicatorStore.updateFilter(this.filterQuery);
    }, 10);
  }

  /**
   * Updates the filter query based on the state of the TriStateCheckboxChangeEvent.
   * The method adjusts the `ids` property of `filterQuery` depending on whether the state is `null`, `true`, or `false`.
   *
   * @param $event The change event object containing the new value of the TriStateCheckbox.
   * @return void This method does not return a value.
   */
  setHasDataFilter($event: TriStateCheckboxChangeEvent): void {
    const state: boolean | null = $event.value;
    switch (state) {
      case null:
        this.filterQuery.ids = undefined;
        break;
      case true:
        this.filterQuery.ids = this.dataStore.getIndicatorIdsWithData();
        break;
      case false:
        this.filterQuery.ids = this.getIndicatorIdsWithoutData();
        break;
    }

    this.updateFilter();
  }

  /**
   * Retrieves a list of indicator IDs that do not have associated data.
   *
   * This method compares the list of all indicator IDs with the list of IDs
   * that currently have data and returns only the IDs that are missing data.
   *
   * @return {string[]} An array of indicator IDs that do not have associated data.
   */
  getIndicatorIdsWithoutData(): string[] {
    const idsWithData = this.dataStore.getIndicatorIdsWithData();
    const indicatorIds = this.indicatorStore.allIds();
    return indicatorIds.filter(id => !idsWithData.includes(id));
  }
}
